import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { Loader } from "../../../../../MainComponents";

const Question = ({ state, dispatch, exam, isParagraphe }) => {
  const { ID } = useParams();

  // State management
  const [hiddenAnswers, setHiddenAnswers] = useState({});
  const [isAbcActive, setIsAbcActive] = useState(false);

  const currentNSection =
    state.questions[state.currentQuestionIndex]?.n_section;

  const currentQuestions = isParagraphe
    ? state.questions.filter((q) => q.n_section === currentNSection)
    : [state.questions[state.currentQuestionIndex]];

  const checkApi = process.env.REACT_APP_STUDENT_TRIAL_EXAM_UPDATE_API;

  // Reset hidden answers when ABC mode is disabled
  useEffect(() => {
    if (!isAbcActive) {
      setHiddenAnswers({});
    }
  }, [isAbcActive]);

  // Toggle answer visibility in ABC mode
  const toggleHideAnswer = async (questionId, answerOrder, n) => {
    const key = `${questionId}-${answerOrder}`;

    setHiddenAnswers((prev) => {
      const isNowHidden = !prev[key];

      if (isNowHidden) {
        const answerData = {
          id: questionId,
          order: "E",
          lecture_id: ID,
          n,
        };

        axios
          .post(checkApi, answerData)
          .catch((err) => console.error("Error resetting answer:", err));
        dispatch({ type: "setAnswer", payload: answerData });
      }

      return {
        ...prev,
        [key]: isNowHidden,
      };
    });
  };
  // Handle multiple choice answer selection
  const handleSelectAnswer = async (answer, questionId, n) => {
    dispatch({
      type: "setAnswer",
      payload: { id: questionId, order: answer, n },
    });

    try {
      await axios.post(checkApi, {
        id: questionId,
        order: answer,
        lecture_id: ID,
        n: n,
      });
    } catch (error) {
      console.error("Error updating answer:", error);
      toast.error(error.response?.data?.message || "Failed to save answer");
    }
  };

  // Toggle question flag status
  const handleFlagged = (currentQuestion) => {
    dispatch({ type: "toggleFlagQuestion", payload: currentQuestion.id });
  };

  // Toggle ABC mode
  const handleAbcToggle = () => {
    setIsAbcActive(!isAbcActive);
    if (!isAbcActive) {
      setHiddenAnswers({});
    }
  };

  // Render question header
  const renderQuestionHeader = (currentQuestion, idx) => (
    <div className="border-line question-index-container flex items-center justify-between bg-gray-200 rounded mb-2 top-0 z-5">
      <div className="flex gap-5 items-center h-full">
        <p className="p-6 font-semibold bg-black text-white text-2xl h-full">
          {currentQuestion.n}
        </p>
        <button
          onClick={() => handleFlagged(currentQuestion)}
          className="flex items-center text-2xl text-gray-600 hover:text-black mr-2 h-full"
        >
          <svg
            fill="currentColor"
            viewBox="0 0 24 24"
            className={`w-10 h-10 ${
              currentQuestion?.flagged ? "text-yellow-500" : "text-gray-500"
            }`}
          >
            <path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.807-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"></path>
          </svg>
          <span className="ml-1">
            {currentQuestion?.flagged ? "Unmark" : "Mark for Review"}
          </span>
        </button>
      </div>
      {currentQuestion?.answers && (
        <button
          onClick={handleAbcToggle}
          className={`flex items-center text-sm mr-2 h-full ${
            isAbcActive ? "text-white" : "text-gray-600 hover:text-black"
          }`}
        >
          <div
            className={`p-7 relative border border-gray-300 rounded-md w-8 h-8 flex items-center justify-center transition-colors duration-200 ${
              isAbcActive ? "bg-blue-500 text-white" : "bg-transparent"
            }`}
          >
            <span
              className={`text-[12px] font-medium ${
                isAbcActive ? "text-white" : "text-gray-600"
              }`}
            >
              ABC
            </span>
            {!isAbcActive && (
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                className="absolute w-8 h-8 text-gray-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M18 6L6 18"
                />
              </svg>
            )}
          </div>
        </button>
      )}
    </div>
  );

  // Render multiple choice answers
  const renderMultipleChoiceAnswers = (currentQuestion) => (
    <div className="space-y-4">
      {currentQuestion?.answers.map((answer, i) => {
        const answered = state.questions.find(
          (q) => q.id === currentQuestion.id
        );
        const isSelected = answered?.order === answer.order;
        const isHidden = hiddenAnswers[`${currentQuestion.id}-${answer.order}`];

        return (
          <div
            key={i}
            className={`group relative rounded-2xl transition-all duration-200 hover:shadow-md ${
              isSelected
                ? "border-blue-800 border-4"
                : "border-black hover:bg-gray-300 border-2"
            }`}
          >
            <div className="flex items-center">
              <div
                className="flex-1 p-5 cursor-pointer"
                onClick={() => {
                  if (!isHidden) {
                    handleSelectAnswer(
                      answer.order,
                      currentQuestion.id,
                      currentQuestion.n
                    );
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <span
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-2xl transition-colors ${
                      isSelected
                        ? "bg-blue-800 text-white"
                        : "text-black border-black border"
                    }`}
                  >
                    {answer.order}
                  </span>
                  <div className="relative flex-1 leading-relaxed text-lg">
                    <div
                      className={`transition-opacity duration-300 text-black text-3xl ${
                        isHidden ? "opacity-30 grayscale" : "opacity-100"
                      }`}
                      dangerouslySetInnerHTML={{ __html: answer.answer }}
                    />
                    {isHidden && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-full h-4 border-t border-dashed border-gray-800" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isAbcActive && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHideAnswer(currentQuestion.id, answer.order);
                  }}
                  className={`m-4 w-12 h-12 rounded-full relative text-black font-bold text-lg transition-all duration-200 hover:scale-105 ${
                    isHidden ? "hover:underline" : "border border-black"
                  }`}
                >
                  {!isHidden && (
                    <svg
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="w-5 h-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45"
                    >
                      <line x1="0" y1="10" x2="20" y2="10"></line>
                    </svg>
                  )}
                  {isHidden ? "undo" : answer.order}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (
    !currentQuestions ||
    currentQuestions.length === 0 ||
    !currentQuestions[0]
  ) {
    return <Loader />;
  }

  return (
    <div className="w-full  mx-auto bg-white rounded-2xl px-2 h-full overflow-y-auto   ">
      <div className="w-3/4 mx-auto py-5  ">
        {currentQuestions?.map((currentQuestion, idx) => (
          <div key={currentQuestion.id} className="mb-12">
            {renderQuestionHeader(currentQuestion, idx)}
            <div className="mb-8">
              <div
                className="flex-1 leading-relaxed text-gray-800 font-medium"
                dangerouslySetInnerHTML={{ __html: currentQuestion.question }}
              />
            </div>
            {renderMultipleChoiceAnswers(currentQuestion)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Question;
